/* =====================================================================
   NINE BY SILVERLINING — progressive enhancements
   Loaded as a module after {{ content_for_header }}. Pure vanilla,
   no dependency on Horizon internals. Every feature degrades gracefully.
   ===================================================================== */

/* Reveal-on-scroll for .nine-reveal elements */
function initReveal() {
  const els = document.querySelectorAll('.nine-reveal');
  if (!els.length) return;

  if (!('IntersectionObserver' in window)) {
    els.forEach((el) => el.classList.add('is-in'));
    return;
  }

  const io = new IntersectionObserver(
    (entries, obs) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-in');
          obs.unobserve(entry.target);
        }
      });
    },
    { threshold: 0.12, rootMargin: '0px 0px -8% 0px' }
  );

  els.forEach((el) => io.observe(el));
}

/* Lightweight testimonial carousel: [data-nine-carousel] with
   [data-nine-track] children and optional prev/next controls. */
function initCarousels() {
  const carousels = document.querySelectorAll('[data-nine-carousel]');
  carousels.forEach((root) => {
    const track = root.querySelector('[data-nine-track]');
    if (!track) return;
    const prev = root.querySelector('[data-nine-prev]');
    const next = root.querySelector('[data-nine-next]');

    const step = () => {
      const first = track.querySelector('*');
      if (!first) return track.clientWidth * 0.8;
      const gap = parseFloat(getComputedStyle(track).columnGap || '0') || 0;
      return first.getBoundingClientRect().width + gap;
    };

    prev?.addEventListener('click', () =>
      track.scrollBy({ left: -step(), behavior: 'smooth' })
    );
    next?.addEventListener('click', () =>
      track.scrollBy({ left: step(), behavior: 'smooth' })
    );
  });
}

/* Lookbook: batched reveal + full-screen viewer.
   Root: [data-nine-lookbook] containing [data-nine-lb-item] frames.
   Everything here is enhancement — with JS off the grid is already complete. */
function initLookbook() {
  const roots = document.querySelectorAll('[data-nine-lookbook]');

  roots.forEach((root) => {
    /* The theme editor re-renders sections; never bind a section twice. */
    if (root.dataset.nineLbReady === '1') return;
    root.dataset.nineLbReady = '1';

    const items = Array.from(root.querySelectorAll('[data-nine-lb-item]'));
    if (!items.length) return;

    const looks = items.map((el) => ({
      el,
      src: el.dataset.nineLbSrc,
      alt: el.dataset.nineLbAlt || '',
    }));

    /* ---- Batched reveal ------------------------------------------------ */
    const moreBtn = root.querySelector('[data-nine-lb-more]');
    const batch = parseInt(root.dataset.batch || '0', 10);

    if (moreBtn && batch > 0) {
      moreBtn.addEventListener('click', () => {
        const held = items.filter((el) => el.classList.contains('is-held'));
        const next = held.slice(0, batch);

        next.forEach((el) => el.classList.remove('is-held'));
        /* Held frames were display:none, so the reveal observer never saw
           them — settle them in on the next frame instead. */
        requestAnimationFrame(() => next.forEach((el) => el.classList.add('is-in')));

        if (held.length <= batch) moreBtn.closest('.nine-lookbook__more')?.remove();
        next[0]?.querySelector('.nine-lb-item__trigger')?.focus({ preventScroll: true });
      });
    }

    /* ---- Viewer -------------------------------------------------------- */
    const viewer = root.parentElement?.querySelector('[data-nine-lb-viewer]')
      || document.querySelector('[data-nine-lb-viewer]');
    if (!viewer) return;

    const img = viewer.querySelector('[data-nine-lb-img]');
    const counter = viewer.querySelector('[data-nine-lb-counter]');
    const closeBtn = viewer.querySelector('[data-nine-lb-close]');
    let current = -1;
    let lastFocus = null;
    let prevOverflow = '';

    const preload = (i) => {
      const look = looks[i];
      if (!look) return;
      const ghost = new Image();
      ghost.src = look.src;
    };

    const show = (i) => {
      const total = looks.length;
      current = ((i % total) + total) % total;
      const look = looks[current];
      img.src = look.src;
      img.alt = look.alt;
      if (counter) counter.textContent = `${current + 1} / ${total}`;
      preload(current + 1);
      preload(current - 1);
    };

    const open = (i) => {
      lastFocus = document.activeElement;
      prevOverflow = document.documentElement.style.overflow;
      document.documentElement.style.overflow = 'hidden';
      viewer.hidden = false;
      show(i);
      requestAnimationFrame(() => viewer.classList.add('is-open'));
      closeBtn?.focus({ preventScroll: true });
    };

    const close = () => {
      viewer.classList.remove('is-open');
      document.documentElement.style.overflow = prevOverflow;
      const finish = () => {
        viewer.hidden = true;
        img.src = '';
      };
      /* Match the CSS fade; fall through immediately if motion is reduced. */
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) finish();
      else setTimeout(finish, 350);
      current = -1;
      lastFocus?.focus({ preventScroll: true });
    };

    looks.forEach((look, i) => {
      look.el
        .querySelector('.nine-lb-item__trigger')
        ?.addEventListener('click', () => open(i));
    });

    closeBtn?.addEventListener('click', close);
    viewer.querySelector('[data-nine-lb-prev]')?.addEventListener('click', () => show(current - 1));
    viewer.querySelector('[data-nine-lb-next]')?.addEventListener('click', () => show(current + 1));
    viewer.addEventListener('click', (e) => {
      if (e.target === viewer) close();
    });

    document.addEventListener('keydown', (e) => {
      if (viewer.hidden || current < 0) return;
      if (e.key === 'Escape') close();
      else if (e.key === 'ArrowLeft') show(current - 1);
      else if (e.key === 'ArrowRight') show(current + 1);
      else return;
      e.preventDefault();
    });
  });
}

function boot() {
  initReveal();
  initCarousels();
  initLookbook();
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', boot);
} else {
  boot();
}

/* Re-run when Shopify theme editor re-renders a section */
document.addEventListener('shopify:section:load', boot);
